import React, { useState } from "react";
import Input from "../../../components/input";
import { ISignupDetails, TKey } from "../../../types";
import Button from "../../../components/ui/button";
import {
  checkPasswords,
  displayErrorMsg,
} from "../../../utils/displayErrorMsg";
import { validateEmail } from "../../../utils/validateEmail";
import { validatePassword } from "../../../utils/validatePassword";
import colors from "../../../utils/colors";
import { SetURLSearchParams, useNavigate } from "react-router-dom";
import { useCreateDoctor } from "../../../apis/doctor";
import customToast, { ToastType } from "../../../components/Toast";
import { UseMutateFunction } from "@tanstack/react-query";
import { TCreateDoctor } from "../../../apis/types";
import { Checkbox } from "@chakra-ui/react";
import {
  useUploadDoctorMedia,
  useAttachMediaToDoctor,
} from "../../../apis/doctor";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { setDoctorInfo } from "../../../store/authSlice";

interface IProps {
  setStep: React.Dispatch<React.SetStateAction<string>>;
  setSearchParams: SetURLSearchParams;
  setDetails: React.Dispatch<React.SetStateAction<ISignupDetails>>;
  details: ISignupDetails;
  updateState: (name: TKey, value: string) => void;
  createDoctor: UseMutateFunction<any, Error, TCreateDoctor, unknown>;
  createDoctorLoading: boolean;
}

const SignupForm = ({
  setStep,
  setSearchParams,
  details,
  setDetails,
  updateState,
  createDoctor,
  createDoctorLoading,
}: IProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const { mutateAsync: uploadDoctorMedia } = useUploadDoctorMedia();
  const { mutateAsync: attachDoctorMedia } = useAttachMediaToDoctor();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const isDisabled =
    !details?.fullName ||
    !validateEmail(details?.email) ||
    !validatePassword(details?.password) ||
    !(details?.confirmPassword === details?.password) ||
    !agreed;

  const labelStyles =
    "!font-lato !font-[400] !text-[14px] md:!text-[18px] !leading-[21.6px]";
  const inputStyles =
    " !p-[14px] md:!p-[20px] !rounded-[8px]  !text-[14px] md:!text-[18px] !leading-[21.6px]";

  return (
    <div className="w-full flex gap-[24px] flex-col">
      {/* NEW: Title (optional) */}
      <div>
        <Input
          handleChange={(e) => updateState("title", e)}
          property="title"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.title || ""}
          title="Title (optional)"
          placeholder='e.g. "Dr.", "Nurse", "Pharmacist", "Nutritionist"'
        />
      </div>

      <div>
        <Input
          handleChange={(e) => updateState("fullName", e)}
          property="fullName"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.fullName}
          title="Full name"
          placeholder="Enter full name"
        />
        {displayErrorMsg(
          "Full name can not be empty",
          details.fullName,
          submitting,
        )}
      </div>

      <div>
        <Input
          handleChange={(e) => updateState("email", e)}
          property="email"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.email}
          title="Email"
          placeholder="Enter email"
        />
        {displayErrorMsg("email can not be empty", details.email, submitting)}
        {details?.email && !validateEmail(details.email) && (
          <div className="text-[red] text-left text-[11px]">
            Enter a valid email
          </div>
        )}
      </div>

      <div>
        <Input
          handleChange={(e) => updateState("password", e)}
          property="password"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.password}
          title="Password"
          isPassword
          placeholder="Enter password"
        />
        {displayErrorMsg(
          "Password can not be empty",
          details.password,
          submitting,
        )}
        {details?.password && !validatePassword(details.password) && (
          <div className="text-[red] text-left text-[11px]">
            Password must be 6 digits or more
          </div>
        )}
      </div>

      <div>
        <Input
          handleChange={(e) => updateState("confirmPassword", e)}
          property="confirmPassword"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.confirmPassword}
          title="Confirm Password"
          isPassword
          placeholder="Re-enter password"
        />
        {displayErrorMsg(
          "Confirm Password can not be empty",
          details.confirmPassword,
          submitting,
        )}
        {checkPasswords(
          details?.password,
          details?.confirmPassword,
          "Passwords do not match",
        )}
      </div>

      <div>
        <label
          className={labelStyles}
          style={{ display: "block", marginBottom: "8px" }}
        >
          Upload Profile Picture (optional)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setProfilePicture(file);
          }}
          className="text-sm"
        />
      </div>

      <div>
        <div
          className="font-lato  !text-[14px] md:!text-[18px] font-[400] leading-normal flex gap-[16px] leading-[21.6px]"
          style={{ color: colors.dark }}
        >
          <Checkbox onChange={() => setAgreed((prev) => !prev)} />

          <div>
            I agree to the{" "}
            <span
              style={{ color: colors.primary, textDecoration: "underline" }}
            >
              Terms of Use
            </span>{" "}
            and{" "}
            <span
              style={{ color: colors.primary, textDecoration: "underline" }}
            >
              Privacy Policy
            </span>
          </div>
        </div>
        <div className="mt-1">
          {displayErrorMsg(
            "You have to agree to terms and condition",
            agreed,
            submitting,
          )}
        </div>
      </div>

      <Button
        theme="primary"
        text="Continue"
        className="w-full rounded-md"
        onClick={async () => {
          setSubmitting(true);

          if (isDisabled) return;

          createDoctor(
            {
              confirmPassword: details?.confirmPassword,
              email: details?.email,
              name: details?.fullName,
              password: details?.password,
              // ⭐ NEW: send title to backend if provided
              title: details?.title || undefined,
            } as TCreateDoctor,
            {
              onSuccess: async (data) => {
                const doctorId = data?.data?._id;

                if (!doctorId) {
                  customToast(
                    "Doctor ID is missing from response",
                    ToastType.error,
                  );
                  return;
                }

                // Store in session & Redux
                sessionStorage.setItem(
                  "created-doctor",
                  JSON.stringify(data.data),
                );
                dispatch(setDoctorInfo(data.data));

                try {
                  if (profilePicture) {
                    const formData = new FormData();
                    formData.append("file", profilePicture);

                    const uploadResponse = await uploadDoctorMedia({
                      doctorId,
                      tag: "profile-picture",
                      formData,
                    });

                    if (!uploadResponse?.url) {
                      throw new Error("Upload succeeded but URL is missing");
                    }

                    await attachDoctorMedia({
                      doctorId,
                      url: uploadResponse.url,
                      tag: "profile-picture",
                    });

                    const updatedDoctorData = {
                      ...data.data,
                      profilePicture: uploadResponse.url,
                    };

                    dispatch(setDoctorInfo(updatedDoctorData));
                    sessionStorage.setItem(
                      "created-doctor",
                      JSON.stringify(updatedDoctorData),
                    );

                    customToast("Profile picture uploaded", ToastType.success);
                  }
                } catch (uploadError) {
                  console.error("Upload/Attach failed", uploadError);
                  customToast(
                    "Doctor created but please upload profile picture on login if not uploaded already",
                    ToastType.error,
                  );
                }

                customToast("User created successfully", ToastType.success);
                setStep("verify-email");
                setSearchParams({ tab: "verify-email" });
              },
              onError: (error) => {
                const message = error.message;
                customToast(message, ToastType.error);
              },
            },
          );
        }}
        loading={createDoctorLoading}
      />

      <p className="font-lato font-[400] text-[14px] md:text-[18px] leading-[21.6px]">
        Already have an account?{" "}
        <span
          className="cursor-pointer  font-[700]"
          style={{ color: colors.primary }}
          onClick={() => navigate("/login")}
        >
          Log in
        </span>
      </p>
    </div>
  );
};

export default SignupForm;
